import React, { useState, useEffect, useRef, useCallback } from 'react';
import * as Inovx from '@shared';
import OntologyActions from '../actions/OntologyActions';
import { ChipListComponent, ChipsDirective, ChipDirective, ClickEventArgs, SelectedItem } from '@syncfusion/ej2-react-buttons';
import ControlPoints from './ControlPoints';

var SystemActions = Inovx.Shared.System.Actions;
var InovxControls = Inovx.Shared.Controls;
var PermissionService = Inovx.Shared.Permission.PermissionService;
var MANAGE_FACILITY_PERMISSION = "Facility.Manage";

interface OntologyInformationProps {
    ontology: IOntologyPart;
    isUniqueFunc: (name: string, id: string) => boolean;
}

const hasManagePermission: boolean = PermissionService.checkPermission(MANAGE_FACILITY_PERMISSION);

const OntologyInformation: React.FC<OntologyInformationProps> = ({ ontology: ontologyParam, isUniqueFunc }) => {
    const gctChipsRef = useRef<ChipListComponent | null>(null);
    const mapChipsRef = useRef<ChipListComponent | null>(null);
    const gpsChipsRef = useRef<ChipListComponent | null>(null);

    const [formData, setFormData] = useState<IOntologyPart>(ontologyParam);

    const [editMode, setEditMode] = useState(false);
    const [edited, setEdited] = useState(false);
    const [hasAddChildFormError, setHasAddChildFormError] = useState(false);

    useEffect(() => {
        setFormData(ontologyParam);
        setEditMode(false);
        setEdited(false);
    }, [ontologyParam]);

    useEffect(() => {
        OntologyActions.setOntologyFormDirty(editMode);
    }, [editMode]);

    const validateFormData = (data: IOntologyPart) => {
        let hasError = true;
        const trimmedValue = data.Name.trim();
        if (trimmedValue.length > 0) {
            // check if the name is unique among the siblings, skip current node
            const isUnique = isUniqueFunc(trimmedValue, data.ID);
            if (isUnique) {
                hasError = false;
            }
        }
        setHasAddChildFormError(hasError);
    }

    const handleEdit = () => {
        setEditMode(true);
        setEdited(false); // Ensure Save is disabled when entering edit mode
        validateFormData(formData);
    };

    const cancelEdit = (resetData: boolean) => {
        if (resetData) {
            setFormData(ontologyParam);
        }
        setEditMode(false);
        setEdited(false);
        setHasAddChildFormError(false);
    };

    const handleSave = async () => {
        if (hasAddChildFormError || !edited) {
            return;
        }
        if (formData.MapType !== ontologyParam?.MapType) {
            formData.Map = null;
        }
        if (formData.GpsTransformationType === 'transformation') {
            if (!areControlPointsValid(formData.GpsTransformation)) {
                SystemActions.displayWarningMessage({ message: InovxLoader.t("facility:message.pleaseEnterValidValueForAllControlPoints") });
                return;
            }
        }
        await OntologyActions.editOntologyPart(formData);
        cancelEdit(false);
    };

    const handleCancel = () => {
        if (edited) {
            SystemActions.displaySaveChangesDialog({
                message: InovxLoader.t("common:msgBox.saveChanges"),
                confirmCallback: async () => {
                    await handleSave();
                },
                cancelCallback: () => {
                    cancelEdit(true);
                }
            });
        } else {
            cancelEdit(true);
        }
    };

    // Map TransformationType to chip index
    const getGctChipIndex = (transformationType: TransformationType): number => {
        switch (transformationType) {
            case "none":
                return 0;
            case "transformation":
                return 1;
            case "inherit":
                return 2;
            default:
                return 0;
        }
    }

    // Map MapType to chip index
    const getMapChipIndex = (mapType: MapType): number => {
        switch (mapType) {
            case "image":
                return 0;
            case "provider":
                return 1;
            case "inherit":
                return 2;
            default:
                return 0;
        }
    }

    const getGpsChipIndex = (gpsType: GpsTransformationType): number => {
        switch (gpsType) {
            case "none":
                return -1;
            case "transformation":
                return 0;
            case "inherit":
                return 1;
            default:
                return -1;
        }
    }

    const updateFormData = (newData: IOntologyPart) => {
        validateFormData(newData);
        setFormData(newData);
        setEdited(JSON.stringify(newData) !== JSON.stringify(ontologyParam));
    };

    const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!editMode) return;

        const { name, value } = e.target;
        if (name === "Name") {
            const newFormData = { ...formData, [name]: value };
            updateFormData(newFormData);
        }
    };

    const handleGctChipSelect = (e: ClickEventArgs) => {
        if (!gctChipsRef.current || !editMode) return;

        const chipToType: (TransformationType)[] = ["none", "transformation", "inherit"];
        const selectedChip = gctChipsRef.current.getSelectedChips() as SelectedItem;
        const newType = chipToType[selectedChip.index];
        if (formData.TransformationType !== newType) {
            const newFormData = { ...formData, TransformationType: newType };
            updateFormData(newFormData);
        }
    };

    const handleMapChipSelect = (e: ClickEventArgs) => {
        if (!mapChipsRef.current || !editMode) return;

        const chipToType: MapType[] = ["image", "provider", "inherit"];
        const selectedChip = mapChipsRef.current.getSelectedChips() as SelectedItem;
        const newType = chipToType[selectedChip.index];
        if (formData.MapType !== newType) {
            const newFormData = { ...formData, MapType: newType };
            updateFormData(newFormData);
        }
    };

    const handleGpsChipSelect = (e: ClickEventArgs) => {
        if (!gpsChipsRef.current || !editMode) return;

        const chipToType: GpsTransformationType[] = ["transformation", "inherit"];
        const selectedChip = gpsChipsRef.current.getSelectedChips() as SelectedItem;
        const newType = chipToType[selectedChip.index];
        if (formData.GpsTransformationType !== newType) {
            const newFormData = { ...formData, GpsTransformationType: newType };
            updateFormData(newFormData);
        }
    };

    // Prevent deselection of chip by always keeping one selected
    const handleBeforeGctChipSelect = (e: ClickEventArgs) => {
        if (!gctChipsRef.current) return;

        const selectedChip = gctChipsRef.current.getSelectedChips() as SelectedItem;
        if (selectedChip) {
            if (e.index === selectedChip.index) {
                e.cancel = true; // Prevent deselection
            }
        }
    };

    const handleBeforeMapChipSelect = (e: ClickEventArgs) => {
        if (!mapChipsRef.current) return;

        const selectedChip = mapChipsRef.current.getSelectedChips() as SelectedItem;
        if (selectedChip) {
            if (e.index === selectedChip.index) {
                e.cancel = true; // Prevent deselection
            }
        }
    };

    const handleBeforeGpsChipSelect = (e: ClickEventArgs) => {
        if (!gpsChipsRef.current) return;

        const selectedChip = gpsChipsRef.current.getSelectedChips() as SelectedItem;
        if (selectedChip) {
            if (e.index === selectedChip.index) {
                e.cancel = true;
            }
        }
    };

    function areControlPointsValid(gps: IGpsTransformation | null): boolean {
        const points = gps?.ControlPoints;
        if (!points || points.length < 3) return false;

        return points.every(p =>
            [p.Wgs84Lat, p.Wgs84Long, p.Wgs84Alt].every(v => v !== 0 && v !== null) &&
            [p.FacilityN, p.FacilityE, p.FacilityU].every(v => v !== null)
        );
    }

    const updateControlPoints = useCallback((controlPoints: IGpsControlPoint[]) => {
        setEdited(true);
        setFormData(prev => ({
            ...prev,
            GpsTransformation: {
                ...prev.GpsTransformation,
                ControlPoints: controlPoints
            }
        }));
    }, []);

    const selectedGctChipIndex = getGctChipIndex(formData.TransformationType);
    const selectedMapChipIndex = getMapChipIndex(formData.MapType);
    const selectedGpsChipIndex = getGpsChipIndex(formData.GpsTransformationType);
    const isRootOntology = formData.IsRoot;
    const gctChipListKey = editMode ? `edit-chips-${formData.ID}` : `view-chips-${formData.ID}`;
    const mapChipListKey = editMode ? `edit-map-chips-${formData.ID}` : `view-map-chips-${formData.ID}`;
    const gpsChipListKey = editMode ? `edit-gps-chips-${formData.ID}` : `view-gps-chips-${formData.ID}`;

    return (
        <div className="ontology-information">
            <div className="ontology-form-row">
                <InovxControls.FormField title={`${InovxLoader.t("facility:form.name2")}`} inputName='Name' className={hasAddChildFormError ? 'has-error' : ''} >
                    <input type="text" onChange={handleTextChange} value={formData.Name} className="form-control" name="Name" placeholder={InovxLoader.t("facility:form.hierarchyPartNamePlaceholder")}
                        readOnly={!editMode || isRootOntology} />
                </InovxControls.FormField>
            </div>
            <fieldset className="ontology-form-fieldset">
                <legend>{InovxLoader.t("facility:hierarchy.graphicContentTransformation")}</legend>
                <div>
                    <ChipListComponent
                        ref={gctChipsRef}
                        key={gctChipListKey}
                        selection="Single"
                        selectedChips={[selectedGctChipIndex]}
                        beforeClick={handleBeforeGctChipSelect}
                        click={handleGctChipSelect}
                    >
                        <ChipsDirective>
                            <ChipDirective text={InovxLoader.t("facility:hierarchy.gctNone")}
                                enabled={(editMode && isRootOntology) || selectedGctChipIndex === 0} />
                            <ChipDirective text={InovxLoader.t("facility:hierarchy.gctTransformation")}
                                enabled={/*(editMode) || */selectedGctChipIndex === 1} />
                            <ChipDirective text={InovxLoader.t("facility:hierarchy.gctInheritFromParent")}
                                enabled={(editMode && !isRootOntology) || selectedGctChipIndex === 2} />
                        </ChipsDirective>
                    </ChipListComponent>
                </div>
            </fieldset>
            <fieldset className="ontology-form-fieldset">
                <legend>{InovxLoader.t("facility:hierarchy.mapInformation")}</legend>
                <div>
                    <ChipListComponent
                        key={mapChipListKey}
                        selection="Single"
                        selectedChips={[selectedMapChipIndex]}
                        beforeClick={handleBeforeMapChipSelect}
                        click={handleMapChipSelect}
                        ref={mapChipsRef}
                    >
                        <ChipsDirective>
                            <ChipDirective text={InovxLoader.t("facility:hierarchy.mapImage")}
                                enabled={(editMode) || selectedMapChipIndex === 0} />
                            <ChipDirective text={InovxLoader.t("facility:hierarchy.mapProvider")}
                                enabled={/*(editMode) || */selectedMapChipIndex === 1} />
                            <ChipDirective text={InovxLoader.t("facility:hierarchy.mapInheritFromParent")}
                                enabled={(editMode && !isRootOntology) || selectedMapChipIndex === 2} />
                        </ChipsDirective>
                    </ChipListComponent>
                </div>
            </fieldset>
            <fieldset className="ontology-form-fieldset">
                <legend>{InovxLoader.t("facility:hierarchy.gpsTransformation")}</legend>
                <div>
                    <ChipListComponent
                        key={gpsChipListKey}
                        selection="Single"
                        selectedChips={[selectedGpsChipIndex]}
                        beforeClick={handleBeforeGpsChipSelect}
                        click={handleGpsChipSelect}
                        ref={gpsChipsRef}
                    >
                        <ChipsDirective>
                            <ChipDirective text={InovxLoader.t("facility:hierarchy.controlPoints")}
                                enabled={(editMode) || selectedGpsChipIndex === 0} />
                            <ChipDirective text={InovxLoader.t("facility:hierarchy.gpsInheritFromParent")}
                                enabled={(editMode && !isRootOntology) || selectedGpsChipIndex === 1} />
                        </ChipsDirective>
                    </ChipListComponent>
                </div>
                {formData.GpsTransformationType === 'transformation' && <ControlPoints editMode={editMode} onControlPointChange={updateControlPoints} gpsControlPoints={formData.GpsTransformation?.ControlPoints} />}
            </fieldset>
            {hasManagePermission && <div className="ontology-actions">
                {!editMode ? (
                    <a id="facilityApp-edit-btn" onClick={() => handleEdit()} className="btn btn-primary" >{InovxLoader.t("facility:preferences.editButton")}</a>
                ) : (
                    <div>
                        <div className="d-flex j-cc">
                            <div>
                                <a id="facilityApp-save-btn" onClick={() => handleSave()} className={`btn btn-primary${!edited || hasAddChildFormError ? ' disabled' : ''}`}>{InovxLoader.t("facility:preferences.saveButton")}</a>
                            </div>
                            <div className="ml-1">
                                <a id="facilityApp-cancel-btn" onClick={() => handleCancel()} className="btn btn-default">{InovxLoader.t("facility:preferences.cancelButton")}</a>
                            </div>
                        </div>
                    </div>
                )}
            </div>
            }
        </div>
    );
};

export default OntologyInformation;
